import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import DateInput from '../ui/date-input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import MobileInput from '../ui/mobile-input';
import { validateMobile } from '../../utils/phoneValidation';
import { validateDate } from '../../utils/dateValidation';
import { INDIAN_STATES } from '../../data/indianStates';

const dialogClass = 'sm:max-w-lg max-h-[90vh] overflow-y-auto w-[calc(100vw-2rem)]';

export function buildFormFromEmployee(row) {
  return {
    first_name: row.first_name || '',
    last_name: row.last_name || '',
    email: row.email || '',
    phone: row.phone || '',
    designation: row.designation || '',
    address: row.address || '',
    joining_date: row.joining_date || '',
    department_id: row.department_id ? String(row.department_id) : '',
    crm_role_id: row.crm_role_id ? String(row.crm_role_id) : '',
    salary_type: row.salary_type || 'fixed_plus_incentive',
    base_salary: row.base_salary ?? '',
    incentive_per_join: row.incentive_per_join ?? '',
    monthly_target: row.monthly_target ?? '',
    reports_to: row.reports_to || '',
    territories: row.territories?.length ? row.territories : [{ state: '', district: '', city: '' }],
  };
}

export default function CrmEmployeeEditDialog({
  open,
  onOpenChange,
  employee,
  onSaved,
}) {
  const { toast } = useToast();
  const [form, setForm] = useState(buildFormFromEmployee(employee || {}));
  const [saving, setSaving] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [joiningDateError, setJoiningDateError] = useState('');
  const [departments, setDepartments] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);

  useEffect(() => {
    if (open && employee) {
      setForm(buildFormFromEmployee(employee));
      setPhoneError('');
      setJoiningDateError('');
    }
  }, [open, employee]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/crm/departments');
        if (cancelled) return;
        setDepartments(res.data?.data?.items || []);
      } catch {
        if (!cancelled) setDepartments([]);
      }
      const deptId = employee?.department_id ? String(employee.department_id) : '';
      if (deptId) {
        try {
          const res = await api.get('/crm/roles', { params: { department_id: deptId } });
          if (cancelled) return;
          setRoleOptions((res.data?.data?.items || []).map((r) => ({ value: String(r.id), label: r.role_name })));
        } catch {
          if (!cancelled) setRoleOptions([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, employee, toast]);

  const onDepartmentChange = async (deptId) => {
    setForm((prev) => ({ ...prev, department_id: deptId, crm_role_id: '' }));
    try {
      const res = await api.get('/crm/roles', { params: { department_id: deptId } });
      const opts = (res.data?.data?.items || []).map((r) => ({ value: String(r.id), label: r.role_name }));
      setRoleOptions(opts);
      if (opts.length) setForm((prev) => ({ ...prev, crm_role_id: opts[0].value }));
    } catch {
      setRoleOptions([]);
    }
  };

  const updateTerritory = (idx, field, value) => {
    const t = [...form.territories];
    t[idx] = { ...t[idx], [field]: value };
    setForm({ ...form, territories: t });
  };

  const save = async () => {
    if (!employee?.id) return;
    if (!form.joining_date) {
      toast({ title: 'Joining date required', description: 'Please select a joining date.', variant: 'destructive' });
      return;
    }
    const dateErr = validateDate(form.joining_date, { required: true });
    if (dateErr) {
      setJoiningDateError(dateErr);
      toast({ title: 'Invalid date', description: dateErr, variant: 'destructive' });
      return;
    }
    const err = validateMobile(form.phone, { required: false });
    if (err) {
      setPhoneError(err);
      toast({ title: 'Invalid mobile number', description: err, variant: 'destructive' });
      return;
    }
    setPhoneError('');
    setJoiningDateError('');
    setSaving(true);
    try {
      const payload = {
        ...form,
        department_id: form.department_id ? Number(form.department_id) : undefined,
        crm_role_id: form.crm_role_id ? Number(form.crm_role_id) : undefined,
        base_salary: parseFloat(form.base_salary) || 0,
        incentive_per_join: parseFloat(form.incentive_per_join) || 0,
        monthly_target: parseInt(form.monthly_target, 10) || 0,
      };
      delete payload.role;
      await api.put(`/crm/employees/${employee.id}`, payload);
      toast({ title: 'Updated', description: 'Employee updated successfully' });
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Save failed', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={dialogClass}>
        <DialogHeader><DialogTitle>Edit Employee</DialogTitle></DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>First name</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
            <div><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
          </div>
          <div><Label>Email</Label><Input type="email" value={form.email} disabled /></div>
          <div><Label>Phone</Label><MobileInput value={form.phone} onChange={(v) => { setForm({ ...form, phone: v }); setPhoneError(''); }} error={phoneError} /></div>
          <div><Label>Designation</Label><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></div>
          <div>
            <Label>Joining date <span className="text-red-500">*</span></Label>
            <DateInput required value={form.joining_date} onChange={(v) => { setForm({ ...form, joining_date: v }); setJoiningDateError(''); }} error={joiningDateError} />
          </div>
          <div><Label>Department</Label>
            <Select value={form.department_id || undefined} onValueChange={onDepartmentChange}>
              <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
              <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={String(d.id)}>{d.department_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Role</Label>
            <Select value={form.crm_role_id || undefined} onValueChange={(v) => setForm({ ...form, crm_role_id: v })} disabled={!form.department_id}>
              <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
              <SelectContent>{roleOptions.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Base salary</Label><Input type="number" value={form.base_salary} onChange={(e) => setForm({ ...form, base_salary: e.target.value })} /></div>
            <div><Label>Incentive/join</Label><Input type="number" value={form.incentive_per_join} onChange={(e) => setForm({ ...form, incentive_per_join: e.target.value })} /></div>
          </div>
          <div><Label>Territory (state)</Label>
            <Select value={form.territories[0]?.state || ''} onValueChange={(v) => updateTerritory(0, 'state', v)}>
              <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
              <SelectContent>{INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>District</Label><Input value={form.territories[0]?.district || ''} onChange={(e) => updateTerritory(0, 'district', e.target.value)} /></div>
            <div><Label>City</Label><Input value={form.territories[0]?.city || ''} onChange={(e) => updateTerritory(0, 'city', e.target.value)} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} className="bg-blue-600" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
